import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { BlogCard } from "@/components/BlogCard";
import { CATEGORIES, POSTS } from "@/data/blog";
import { ArrowRight } from "lucide-react";

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "Lease Guides, Red Flags & Templates — legaldecoder Blog" },
      {
        name: "description",
        content:
          "Plain-English guides on apartment lease red flags, lease clauses explained, and free lease templates. Read before you sign.",
      },
      { property: "og:title", content: "Lease Guides, Red Flags & Templates" },
      {
        property: "og:description",
        content: "Everything renters need to understand a lease before signing.",
      },
      { property: "og:url", content: "https://legaldecoder.io/blog" },
    ],
    links: [{ rel: "canonical", href: "https://legaldecoder.io/blog" }],
    scripts: [
      {
        src: "https://app.trysoro.com/api/embed/3eb293ff-6ce8-4372-8a11-ae20bc25f78c",
        defer: true,
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          url: "https://legaldecoder.io/blog",
          name: "legaldecoder Blog",
          description:
            "Plain-English guides on apartment lease red flags, lease clauses, and free templates.",
          isPartOf: { "@type": "WebSite", name: "legaldecoder", url: "https://legaldecoder.io/" },
        }),
      },
    ],
  }),
  component: BlogIndex,
});

function BlogIndex() {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="border-b border-border py-16">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
              Lease guides & red flags
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
              Plain-English breakdowns of apartment leases — what to refuse, what each clause means,
              and free templates to negotiate from.
            </p>
          </div>
        </section>

        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 className="mb-6 text-sm font-bold uppercase tracking-wider text-muted-foreground">
              Browse by topic
            </h2>
            <div className="mb-12 grid gap-4 md:grid-cols-3">
              {CATEGORIES.map((cat) => (
                <Link
                  key={cat.slug}
                  to="/blog/$category"
                  params={{ category: cat.slug }}
                  className="group rounded-2xl border border-border bg-gradient-subtle p-6 shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover"
                >
                  <h3 className="text-lg font-semibold group-hover:text-primary-glow">
                    {cat.name}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">{cat.description}</p>
                  <div className="mt-3 flex items-center gap-1 text-sm font-medium text-primary-glow">
                    Browse
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </Link>
              ))}
            </div>

            <h2 className="mb-6 text-sm font-bold uppercase tracking-wider text-muted-foreground">
              All articles
            </h2>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {POSTS.map((post) => (
                <BlogCard key={post.slug} post={post} />
              ))}
            </div>

            <div id="soro-blog" className="mt-16" />
          </div>
        </section>

        <Footer />
      </main>
    </div>
  );
}
